import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { pdfjs, Document, Page, PDFPageProxy } from 'react-pdf';
import { Prediction } from './drawPredictions';
import Spinner from '../Spinner';

import styles from './PDFViewer.module.css';
import { debounce, Rotation } from './';
import { ZoomState } from './';

import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
const options = {
  cMapUrl: `//cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/cmaps/`,
  cMapPacked: true,
};

type PDFViewerProps = {
  doc: string;
  zoom?: ZoomState;
  rotation?: Rotation;
  setPageCount: (pages: number) => void;
  onTextSelect?: (data: { text?: string; position?: { left: number; top: number } }) => void;
  onPageChange?: (pages: number[]) => void;
  onRenderSuccess?: (ev: PDFPageProxy) => void;
  containerRef?: React.RefObject<HTMLDivElement>;
  canvasRefs?: React.RefObject<Array<HTMLCanvasElement | null>>;
  predictions?: Prediction[];
};

const PDFViewer = ({
  doc,
  zoom = 1,
  rotation = 0,
  setPageCount,
  onTextSelect,
  onRenderSuccess,
  onPageChange,
  containerRef,
  canvasRefs,
  predictions,
}: PDFViewerProps): JSX.Element => {
  const [pages, setPages] = useState<null | number>(null);
  const [width, setWidth] = useState(600);
  const [internalPDFRotation, setInternalPDFRotation] = useState<number>(0);

  const onDocumentLoadSuccess = useCallback(
    (pdfProxy: pdfjs.PDFDocumentProxy) => {
      pdfProxy.getPage(1).then((page) => setInternalPDFRotation(page.rotate));
      setPageCount(pdfProxy.numPages);
      setPages(pdfProxy.numPages);
      onPageChange?.([0]);
    },
    [onPageChange, setPageCount]
  );

  const docContainerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const container = containerRef?.current;

    function handleScroll() {
      // Minimize unnecessary work if we have no onPageChange callback function
      if (!container || !onPageChange) return;
      const elements = container?.querySelectorAll('.react-pdf__Page');
      // While we'd like to avoid querying inside the handler,
      // setting the elements' sizes right away might not have their fully rendered sizes yet.
      // @TODO: maybe there is a more effective way to set this once we know the pages have rendered?
      const elementPositions = Array.from(elements || []).map((element) => element.getBoundingClientRect());
      const containerRect = container.getBoundingClientRect();

      // Calculate the container's top and bottom positions within the viewport
      const containerTop = containerRect.top;
      const containerBottom = containerRect.bottom;

      const orderedByVisibility = elementPositions
        .map((position, index) => {
          const elementTop = position.top;
          const elementBottom = position.bottom;
          // Calculate the visible area of the element in the container
          const visibleArea = Math.min(elementBottom, containerBottom) - Math.max(elementTop, containerTop);
          return { index, visibleArea };
        })
        .filter((page) => page.visibleArea > 0)
        .sort((a, b) => b.visibleArea - a.visibleArea)
        .map((page) => page.index);

      onPageChange(orderedByVisibility);
    }

    // Avoid triggering changes constantly
    const debounceHandleScroll = debounce(handleScroll, 50);

    container?.addEventListener('scroll', debounceHandleScroll);

    return () => {
      container?.removeEventListener('scroll', handleScroll);
    };
  }, [onPageChange, pages, doc, zoom, rotation]);

  const docBinary = useMemo(() => {
    const docBinary = atob(doc || '');
    return { data: docBinary };
  }, [doc]);

  useEffect(() => {
    function textSelectListener() {
      if (onTextSelect && docContainerRef.current) {
        const selectedText = document.getSelection()?.toString();
        const range = selectedText ? document.getSelection()?.getRangeAt(0) : undefined;
        if (selectedText && range) {
          const commonAncestorContainer = range.commonAncestorContainer;
          if (
            commonAncestorContainer.contains(docContainerRef.current) ||
            docContainerRef.current.contains(commonAncestorContainer)
          ) {
            const containerPosition = docContainerRef.current.getBoundingClientRect();
            const selectionPosition = range.getBoundingClientRect();
            const left = selectionPosition.left - containerPosition.left;
            const top = selectionPosition.top - containerPosition.top;
            onTextSelect({ text: selectedText, position: { left, top } });
          }
        } else {
          onTextSelect({ text: undefined, position: undefined });
        }
      }
    }

    if (onTextSelect) {
      document.addEventListener('selectionchange', textSelectListener);
    }

    return () => {
      if (onTextSelect) {
        document.removeEventListener('selectionchange', textSelectListener);
      }
    };
  }, [onTextSelect]);

  useLayoutEffect(() => {
    if (docContainerRef.current) {
      const containerWidth = (docContainerRef.current.parentElement?.getBoundingClientRect().width || 500) - 90;
      setWidth(containerWidth);
    }
  }, []);

  useLayoutEffect(() => {
    const debouncedHandleResize = debounce(function handleResize() {
      if (docContainerRef.current) {
        const containerWidth = (docContainerRef.current.parentElement?.getBoundingClientRect().width || 500) - 90;
        setWidth(containerWidth);
      }
    }, 30);

    window.addEventListener('resize', debouncedHandleResize);

    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
    };
  }, []);

  const pageClass = rotation ? `${styles.page} ${styles[`rotate${rotation}`]}` : styles.page;

  const pagesWithPredictions = [...new Array(pages)].map((_, index) =>
    predictions?.find((pred) => pred.page === index) === undefined ? index : index + Math.random()
  );

  return (
    <Document
      file={docBinary}
      onLoadSuccess={onDocumentLoadSuccess}
      onSourceSuccess={() => setPages(null)}
      loading={<Spinner />}
      options={options}
      inputRef={(ref) => (docContainerRef.current = ref)}
      rotate={rotation !== 0 ? rotation + internalPDFRotation : undefined}
      className={styles.document}
    >
      {[...new Array(pages)].map((_page, pageIndex) => {
        return (
          <Page
            pageIndex={pageIndex}
            canvasRef={(el) => {
              if (canvasRefs?.current) {
                canvasRefs.current[pageIndex] = el;
              }
            }}
            width={width}
            // Nasty hack to force rerender on whichever page has new predictions
            key={pagesWithPredictions[pageIndex]}
            onRenderSuccess={onRenderSuccess}
            className={pageClass}
            scale={zoom}
          />
        );
      })}
    </Document>
  );
};

export default PDFViewer;
